import { useCallback, useEffect, useState } from "react";
import { useNavigate, Outlet, useParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { AppMainHeader } from "@/components/AppMainHeader";
import { MobileBackBar } from "@/components/MobileBackBar";
import { getDotPatternStyle } from "@/components/AppBackground";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { getRevisiones, createRevision } from "@/lib/api";
import { useUnreadRevisiones } from "@/contexts/UnreadRevisionesContext";

function nonEmptyString(v) {
  return typeof v === "string" && v.trim() !== "";
}

function sidebarTituloFromPreview(preview, listTitulo) {
  if (nonEmptyString(preview?.titulo)) return preview.titulo;
  return listTitulo || "Sin título";
}

function sidebarEmojiFromPreview(preview, listEmoji, fallback) {
  if (nonEmptyString(preview?.emoji)) return preview.emoji;
  return listEmoji ?? fallback;
}

function formatFormularioFecha(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}h | ${day}.${month}.${year}`;
}

export default function RevisionesLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const inRevisionesSection = (location.pathname || "").startsWith("/revisiones");
  const { id: currentId } = useParams();
  const { user, authLoading } = useAuth();
  const settings = useSettings();
  const [revisiones, setRevisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewUpdates, setPreviewUpdates] = useState({});
  const { unreadByRevision } = useUnreadRevisiones();
  const EMOJI_PREDETERMINADO = "📝";

  useEffect(() => {
    if (authLoading) return;
    if (!user) return navigate("/login", { replace: true });
    if (user.permissions?.revisiones === false) {
      if (user.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
      else if (user.permissions?.formularios !== false) navigate("/formularios", { replace: true });
      else if (user.permissions?.onboarding !== false) navigate("/onboarding", { replace: true });
      else if (user.permissions?.proveedores !== false) navigate("/proveedores", { replace: true });
      else if (user.permissions?.archivos !== false) navigate("/brutalfiles", { replace: true });
      else if (user.permissions?.correos !== false) navigate("/correos", { replace: true });
      else if (user.permissions?.metricas) navigate("/metricas", { replace: true });
      else navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || user.permissions?.revisiones === false) return;
    if (!inRevisionesSection) return;
    setLoading(true);
    getRevisiones()
      .then((data) => setRevisiones(data.revisiones || []))
      .catch(() => {
        toast.error("Error al cargar revisiones");
        setRevisiones([]);
      })
      .finally(() => setLoading(false));
  }, [user, inRevisionesSection]);

  const setRevisionPreview = useCallback((revisionId, data) => {
    setPreviewUpdates((prev) => {
      if (data == null) {
        const next = { ...prev };
        delete next[revisionId];
        return next;
      }
      return { ...prev, [revisionId]: { ...(prev[revisionId] || {}), ...data } };
    });
  }, []);

  const addRevisionToSidebarList = useCallback((item) => {
    if (!item?.id) return;
    setRevisiones((prev) => {
      if (prev.some((r) => r.id === item.id)) return prev;
      const row = {
        id: item.id,
        titulo: item.titulo ?? "",
        slug: item.slug ?? "",
        emoji: item.emoji ?? "📝",
        fecha_creacion: item.fecha_creacion ?? new Date().toISOString(),
      };
      return [row, ...prev];
    });
  }, []);

  const removeRevisionFromSidebarList = useCallback((revisionId) => {
    if (!revisionId) return;
    setRevisiones((prev) => prev.filter((r) => r.id !== revisionId));
    setPreviewUpdates((prev) => {
      if (!prev[revisionId]) return prev;
      const next = { ...prev };
      delete next[revisionId];
      return next;
    });
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const data = await createRevision("Nueva hoja de trabajo");
      if (data?.id) {
        addRevisionToSidebarList({
          id: data.id,
          titulo: data.titulo,
          slug: data.slug,
          fecha_creacion: new Date().toISOString(),
          emoji: "📝",
        });
        toast.success("Hoja de trabajo creada");
        navigate(`/revisiones/${data.id}`);
      } else {
        toast.error("No se pudo crear la hoja");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear la hoja");
    } finally {
      setCreating(false);
    }
  };

  if (authLoading || !user || user.permissions?.revisiones === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const dotStyle = getDotPatternStyle(settings);
  const current = revisiones.find((r) => r.id === currentId);
  const currentPreview = currentId ? previewUpdates[currentId] : null;
  const headerTitulo = nonEmptyString(currentPreview?.titulo)
    ? currentPreview.titulo
    : (current?.titulo || "Editar");
  const titleSuffix = current ? ` / ${headerTitulo}` : "";

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      <AppMainHeader title="Revisiones" titleSuffix={titleSuffix} titleAs="h5" />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className={`${currentId ? "hidden md:flex" : "flex"} w-full md:w-80 shrink-0 flex-col md:border-r border-gray-300 bg-background overflow-hidden`}>
          <div className="p-4 shrink-0 border-b border-gray-200">
            <Button onClick={handleCreate} disabled={creating} className="gap-2 w-full">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Añadir hoja
            </Button>
          </div>
          <div className="p-4 flex-1 overflow-auto mobile-nav-scroll">
            {loading && revisiones.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin shrink-0" />
                <span>Cargando revisiones...</span>
              </div>
            ) : revisiones.length === 0 ? (
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle className="text-base">No hay hojas de trabajo</CardTitle>
                  <CardDescription>Crea la primera hoja y añade sus revisiones.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleCreate} disabled={creating} className="gap-2">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Añadir hoja
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-2 mb-4">
                {revisiones.map((r) => {
                  const preview = previewUpdates[r.id];
                  const emoji = sidebarEmojiFromPreview(preview, r.emoji, EMOJI_PREDETERMINADO);
                  const liveTitle = sidebarTituloFromPreview(preview, r.titulo);
                  const isActive = currentId === r.id;
                  return <li key={r.id}>
                    <Card className={`transition-colors shadow-none hover:bg-muted/50 ${isActive ? "bg-muted/40" : ""}`}>
                      <CardContent className="p-3 py-3">
                        <button
                          type="button"
                          className="flex gap-3 w-full min-w-0 text-left rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onClick={() => navigate(`/revisiones/${r.id}`)}
                        >
                          <div className="w-11 h-11 shrink-0 rounded-lg bg-muted/50 flex items-center justify-center">
                            <span className="text-2xl leading-none select-none inline-block" aria-hidden style={{ textShadow: "rgba(0, 0, 0, 0.3) 0px 1px 2px" }}>
                              {emoji}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                            <p className="text-sm font-medium flex items-center gap-2">
                              <span className="truncate flex-1 min-w-0">{liveTitle}</span>
                              {(unreadByRevision[r.id] || 0) > 0 && (
                                <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-medium shrink-0">
                                  {unreadByRevision[r.id]}
                                </span>
                              )}
                            </p>
                            {r.fecha_creacion && (
                              <p className="text-xs text-muted-foreground">{formatFormularioFecha(r.fecha_creacion)}</p>
                            )}
                          </div>
                        </button>
                      </CardContent>
                    </Card>
                  </li>;
                })}
              </ul>
            )}
          </div>
        </aside>
        <div className={`${currentId ? "" : "hidden md:block"} flex-1 min-w-0 overflow-y-auto relative mobile-nav-scroll`} style={dotStyle}>
          {currentId && <MobileBackBar to="/revisiones" label="Revisiones" />}
          <Outlet
            context={{
              setRevisionPreview,
              addRevisionToSidebarList,
              removeRevisionFromSidebarList,
            }}
          />
        </div>
      </div>
    </div>
  );
}
